import React from 'react';
import { TrendingUp, FileText, Send, Upload, Home, Clock, Phone, PhoneCall } from 'lucide-react';
import { MetricTile } from './MetricTile';
import { motion } from 'framer-motion';
import { LoanStage } from '../data/loans';
import { OfficerData } from '../data/officers';
interface MonthlyScorecardProps {
  officer: OfficerData;
  onTileClick?: (stage: LoanStage) => void;
  onSpeedBonusClick?: () => void;
  onAnswerRateClick?: () => void;
  onDialsClick?: () => void;
}
export function MonthlyScorecard({
  officer,
  onTileClick,
  onSpeedBonusClick,
  onAnswerRateClick,
  onDialsClick
}: MonthlyScorecardProps) {
  const {
    monthlyPoints,
    monthlyGoal,
    creditPulls,
    docOuts,
    submissions,
    closings,
    speedBonusFastCloses,
    speedBonusPoints
  } = officer;
  const remaining = Math.max(0, monthlyGoal - monthlyPoints);
  const percent = Math.round(monthlyPoints / monthlyGoal * 1000) / 10;
  const qualified = monthlyPoints >= monthlyGoal;
  const barWidth = Math.min(100, percent);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.2
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-487" data-mp="3aa6nw543alym">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between gap-3 text-white" data-id="element-488" data-mp="3f2zu6xf7i2ct">
        <div className="flex items-center gap-2 min-w-0" data-id="element-489" data-mp="2rdf7q4jugmys">
          <TrendingUp className="w-5 h-5 shrink-0" data-id="element-490" data-mp="2mec1whw1icpr" />
          <h2 className="font-bold tracking-wide truncate" data-id="element-491" data-mp="2mfn4iedr1efa">
            MONTHLY SCORECARD
          </h2>
        </div>
        <span className="text-xs sm:text-sm font-medium text-blue-200 shrink-0" data-id="element-492" data-mp="30b8t4kg2lw76">
          May 1 – May 31, 2025
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-493" data-mp="fml0n81u2jbi">
        {/* Top Stats */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6" data-id="element-494" data-mp="bm13w5l69hpw">
          <div className="flex-1 min-w-0" data-id="element-495" data-mp="bnu0st5zgnkp">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-496" data-mp="34h111vdin2md">
              Total Points (Month To Date)
            </p>
            <div className="flex items-baseline gap-2 flex-wrap" data-id="element-497" data-mp="39hf9vfmxyglg">
              <span className={`text-5xl sm:text-6xl font-extrabold tracking-tight leading-none ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-498" data-mp="3efgptrp2lzsp">
                {monthlyPoints.toLocaleString()}
              </span>
              <span className="text-sm font-bold text-gray-500" data-id="element-499" data-mp="2qoujq5ymmplc">POINTS</span>
            </div>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-500" data-mp="2uqoi27lcnhic">
            <div className="flex justify-between items-center gap-2 mb-2 pb-2 border-b border-gray-200" data-id="element-501" data-mp="2uolh4sins3yu">
              <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-502" data-mp="22z8kjii98adc">
                Goal
              </span>
              <div className="flex items-baseline gap-1 shrink-0" data-id="element-503" data-mp="270c0s647s8f3">
                <span className="text-lg font-extrabold text-[#0a1e5e]" data-id="element-504" data-mp="26yhfgqp16eah">
                  {monthlyGoal.toLocaleString()}
                </span>
                <span className="text-[10px] font-bold text-gray-500" data-id="element-505" data-mp="1uhzy1pp3brxw">PTS</span>
              </div>
            </div>
            <div className="flex justify-between items-center gap-2 mb-3" data-id="element-506" data-mp="1qfblp81642w6">
              <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-507" data-mp="1licuax8w2a1o">
                Remaining
              </span>
              <div className="flex items-baseline gap-1 shrink-0" data-id="element-508" data-mp="1gj8l46lyscbc">
                <span className={`text-lg font-extrabold ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-509" data-mp="1br8ha32rwz9h">
                  {remaining.toLocaleString()}
                </span>
                <span className="text-[10px] font-bold text-gray-500" data-id="element-510" data-mp="12s3icvnav3ws">PTS</span>
              </div>
            </div>
            <div data-id="element-511" data-mp="xqnz994on7h1">
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-512" data-mp="xr7dofv4jdqb">
                % Of Goal Completed
              </p>
              <p className={`text-xl font-extrabold leading-none ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-513" data-mp="2sum06o95d3it">
                {percent}%
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8" data-id="element-514" data-mp="2j3hs88hw2ixh">
          <div className="flex justify-between items-end mb-2" data-id="element-515" data-mp="2o3nflch4lj9r">
            <span className="text-xs font-bold text-[#0a1e5e] uppercase tracking-wider" data-id="element-516" data-mp="2o22lfnyq42cf">
              Monthly Progress
            </span>
            <span className={`text-sm font-bold ${qualified ? 'text-green-600' : 'text-blue-600'}`} data-id="element-517" data-mp="2e6hmxzsv5k7f">
              {percent}%{qualified ? ' · Goal Met 🎉' : ''}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden" data-id="element-518" data-mp="25631n0fwg53x">
            <div className={`h-4 rounded-full relative ${qualified ? 'bg-green-600' : 'bg-blue-600'}`} style={{
            width: `${barWidth}%`
          }} data-id="element-519" data-mp="3ulix5d0whsbc">
              <div className="absolute right-0 top-0 bottom-0 w-4 bg-white/30" data-id="element-520" data-mp="1nx0i8amwfzcq"></div>
            </div>
          </div>
        </div>

        <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-521" data-mp="1iv1e2yjc4yeq">
          Monthly Breakdown
        </h3>

        {/* Metric Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-id="element-522" data-mp="1suadi6wyz58h">
          <MetricTile title="Call Answer Rate" points={0} count={officer.callAnswerRate} countSuffix="%" countLabel="Answer Rate" icon={Phone} colorClass="text-teal-600" bgClass="bg-teal-100" hidePoints onClick={onAnswerRateClick} data-id="element-523" data-mp="26mgj4z2hxtto" />
          <MetricTile title="Dials" points={0} count={officer.dials} countLabel="Dials" icon={PhoneCall} colorClass="text-indigo-600" bgClass="bg-indigo-100" hidePoints onClick={onDialsClick} data-id="element-524" data-mp="2bjqgd0w4n9s2" />
          <MetricTile title="Credit Pulls" points={750} count={creditPulls} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" ptsPerAction={750} onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-525" data-mp="16yacevryh8xk" />
          <MetricTile title="Doc Outs" points={500} count={docOuts} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" ptsPerAction={500} onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-526" data-mp="1pua7wgk56ulw" />
          <MetricTile title="Submissions" points={500} count={submissions} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" ptsPerAction={500} onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-527" data-mp="28ltaima986ne" />
          <MetricTile title="Closings" points={250} count={closings} countLabel="Closings" icon={Home} colorClass="text-orange-500" bgClass="bg-orange-100" ptsPerAction={250} onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-528" data-mp="3t5olvxuld3js" />
        </div>

        {/* Speed Bonus MTD */}
        {(() => {
        const Wrapper: any = onSpeedBonusClick ? 'button' : 'div';
        return <Wrapper {...onSpeedBonusClick ? {
          type: 'button',
          onClick: onSpeedBonusClick,
          'aria-label': 'View speed bonus loans this month'
        } : {}} className={`group relative mt-auto w-full bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3 transition-all text-left ${onSpeedBonusClick ? 'hover:border-orange-400 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400' : ''}`} data-id="element-529" data-mp="5yw12k6gjpoy">
              <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-530" data-mp="2z6liotvu3lg">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-531" data-mp="qnzau2g01d73">
                  <Clock className="w-6 h-6 text-white" data-id="element-532" data-mp="upt43pnv7ex8" />
                </div>
                <div className="min-w-0" data-id="element-533" data-mp="gpiculin7ybj">
                  <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-534" data-mp="goqhnbsyk2ls">
                    Speed Bonus{' '}
                    <span className="text-gray-500 text-[10px] font-semibold" data-id="element-535" data-mp="lqpo4ujzvfmy">
                      (MTD)
                    </span>
                  </p>
                  <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-536" data-mp="36f3vawo4t9j9">
                    Close a loan within 14 days of credit pull
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0" data-id="element-537" data-mp="2tv6nhwp2hrpy">
                <div className="flex items-baseline gap-1 justify-end" data-id="element-538" data-mp="2tyknwze67qur">
                  <span className="text-2xl sm:text-3xl font-extrabold text-orange-500 leading-none" data-id="element-539" data-mp="37v7zo8as8lkq">
                    +{speedBonusPoints.toLocaleString()}
                  </span>
                  <span className="text-sm font-bold text-gray-500" data-id="element-540" data-mp="2f855tth1wz0t">PTS</span>
                </div>
                <p className="text-[10px] font-bold text-[#0a1e5e] uppercase mt-1" data-id="element-541" data-mp="2b71mgwzsokuu">
                  {speedBonusFastCloses} Fast{' '}
                  {speedBonusFastCloses === 1 ? 'Close' : 'Closes'}
                </p>
              </div>
              {onSpeedBonusClick && <span className="absolute top-2 right-2 text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider" data-id="element-542" data-mp="1wg2m299452mc">
                  View loans →
                </span>}
            </Wrapper>;
      })()}
      </div>
    </motion.div>;
}